/**
 * Main Layout Component
 * Handles split-pane layout with PDF viewer and main content
 * Only active viewer is rendered at a time to prevent WebGL context conflicts
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useApp } from '../../store/AppContext';
import { PdfViewer } from '../viewers/PdfViewer';
import { MatterportViewer } from '../viewers/MatterportViewer';
import { ModelViewer } from '../viewers/ModelViewer';
import { PointCloudViewer } from '../viewers/PointCloudViewer';
import { GeoTiffViewer } from '../viewers/GeoTiffViewer';
import { ThermalViewer } from '../viewers/ThermalViewer';
import { DualViewer } from '../viewers/DualViewer';
import type { TabId } from '../../../shared/types/project';
import './MainLayout.css';

// Viewer component mapping
const ViewerComponents: Record<TabId, React.ComponentType> = {
  matterport: MatterportViewer,
  model: ModelViewer,
  pointcloud: PointCloudViewer,
  geotiff: GeoTiffViewer,
  thermal: ThermalViewer,
};

export function MainLayout() {
  const { state, dispatch } = useApp();
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle resize drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = e.clientX - containerRect.left;
      // Clamp between 200px and 80% of container width
      const maxWidth = containerRect.width * 0.8;
      const clampedWidth = Math.min(maxWidth, Math.max(200, newWidth));
      dispatch({ type: 'SET_PDF_PANEL_WIDTH', payload: clampedWidth });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dispatch]);

  // If no project is selected, this component shouldn't be rendered
  // (App.tsx shows homepage instead), but guard just in case
  if (!state.currentProject) {
    return <main className="main-layout" />;
  }

  return (
    <main
      ref={containerRef}
      className={`main-layout ${isDragging ? 'main-layout--dragging' : ''}`}
    >
      {/* Drag overlay to prevent mouse events being captured by iframes */}
      {isDragging && <div className="main-layout__drag-overlay" />}

      {/* PDF Panel */}
      {state.pdfPanelOpen && (
        <>
          <div
            className="main-layout__pdf-panel"
            style={{ width: state.pdfPanelWidth }}
          >
            <PdfViewer />
          </div>

          <div
            className="main-layout__resizer"
            onMouseDown={handleMouseDown}
          />
        </>
      )}

      {/* Main Viewer Area */}
      <div className="main-layout__viewer">
        {state.dualViewMode ? (
          <DualViewer />
        ) : (
          // Always render only the active viewer
          // Keeping multiple WebGL viewers mounted causes performance issues
          // Data caching happens at the component level, not by keeping them mounted
          <div key={state.activeTab} className="main-layout__viewer-container">
            {React.createElement(ViewerComponents[state.activeTab])}
          </div>
        )}
      </div>
    </main>
  );
}
